import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

const PAGE = 3;

// Etichetta data leggibile Europe/Rome: OGGI · DOMANI · "20 OTT".
const dateLabel = (iso, isToday) => {
  if (isToday) return 'OGGI';
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  const now = new Date();
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return 'DOMANI';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }).toUpperCase().replace('.', '');
};

export const UpcomingAppointmentsV2 = ({ appointments = [], initialLimit = null }) => {
  const navigate = useNavigate();
  const collapsible = initialLimit != null;
  const limit = collapsible ? initialLimit : appointments.length;
  const [visible, setVisible] = useState(limit);

  const shown = collapsible ? appointments.slice(0, visible) : appointments;
  const total = appointments.length;
  const showControl = collapsible && total > limit;

  return (
    <Card className="bg-white rounded-xl border border-[#E4E4E4] shadow-sm h-full flex flex-col" data-testid="direzione-appointments">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#FF7819]" />
          Prossimi appuntamenti
        </CardTitle>
        <Link to="/calendar" className="text-sm text-[#FF7819] hover:underline flex items-center gap-1 shrink-0 whitespace-nowrap" data-testid="appointments-all-link">
          Calendario <ChevronRight className="w-4 h-4" />
        </Link>
      </CardHeader>
      <CardContent className="pt-0 flex-1">
        {total === 0 ? (
          <div className="py-4 text-center" data-testid="appointments-empty">
            <p className="text-sm text-gray-500">Nessun appuntamento programmato</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {shown.map((appt) => (
                <div
                  key={appt.id}
                  onClick={() => navigate(appt.link)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(appt.link); } }}
                  role="button"
                  tabIndex={0}
                  data-testid={`appointment-${appt.id}`}
                  className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors group outline-none focus-visible:ring-2 focus-visible:ring-[#FF7819]/50 ${
                    appt.is_today ? 'bg-orange-50 border border-orange-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${appt.is_today ? 'bg-orange-100' : 'bg-gray-100'}`}>
                      <Calendar className={`w-5 h-5 ${appt.is_today ? 'text-orange-600' : 'text-gray-500'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{appt.client_name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {appt.type_label}
                        {appt.location ? <span className="text-gray-400"> · {appt.location}</span> : null}
                        {appt.assigned_to ? <span className="text-gray-400"> · {appt.assigned_to}</span> : null}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-right">
                    <div>
                      <p className={`text-xs font-semibold ${appt.is_today ? 'text-orange-600' : 'text-gray-400'}`}>
                        {dateLabel(appt.date, appt.is_today)}
                      </p>
                      <p className="text-sm font-medium text-gray-700">{appt.time}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors" />
                  </div>
                </div>
              ))}
            </div>

            {showControl && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500" data-testid="appointments-count">{Math.min(visible, total)} di {total}</span>
                <div className="flex items-center gap-3">
                  {visible < total && (
                    <button onClick={() => setVisible((v) => v + PAGE)} data-testid="appointments-more"
                      className="text-xs font-medium text-[#FF7819] hover:underline flex items-center gap-1">
                      {total - visible === 1 ? 'Mostra altro 1' : `Mostra altri ${Math.min(PAGE, total - visible)}`} <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {visible > limit && (
                    <button onClick={() => setVisible(limit)} data-testid="appointments-less"
                      className="text-xs font-medium text-gray-500 hover:underline flex items-center gap-1">
                      Riduci <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
